import styled from '@emotion/styled'
import { tokens } from '@news-mono/design-tokens'
import { OfferVariantType } from '@west-australian-newspapers/subscription-offer-types'
import {
    breakpoint,
    breakpointMax,
    calcRem,
    colors,
    fonts,
    themedValue,
    transition,
} from '../../../..'
import { zIndex } from '../../../../__styling/settings/z-index'
import { IconSpinner } from '../../../../icons/IconSpinner/IconSpinner'
import { TextLink } from '../../../../typography/TextLink/TextLink'
export interface BreachTypeProps {
    breachType?: OfferVariantType
    useGridLayout?: boolean
    numberOfColumns?: number
}

export const StyledBreachWallContent = styled('div')<{
    backgroundColor?: string
}>(({ theme, backgroundColor }) => ({
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
    alignItems: 'center',
    width: '100%',
    position: 'relative',
    zIndex: zIndex.common.regWall.box,
    fontFamily: theme.fonts.subscriber.text,
    fontSize: calcRem(18),
    color: colors.white,
    padding: calcRem(theme.margins.md, 0),
    backgroundBlendMode: 'overlay, normal',

    backgroundColor:
        backgroundColor ||
        themedValue(theme, {
            thewest: theme.sectionValues.primaryColor,
            perthnow: colors.perthnow.greyBasalt90,
            sevennews: colors.sevennews.charade,
            fallback: undefined,
        }),
    backgroundRepeat: 'no-repeat',
    backgroundSize: '100% auto',
    backgroundPosition: 'top',
    [breakpoint('xxs')]: {
        paddingTop: calcRem(theme.margins.lg),
        paddingBottom: calcRem(theme.margins.md),
    },

    [breakpoint('md')]: {
        backgroundPosition: 'right',
        backgroundSize: 'cover',
    },
}))

interface StyledFullWidthBreachWallBackgroundProps extends BreachTypeProps {
    isFullWidthBelowDesktop?: boolean
}

export const BreachWallWrapper = styled(
    'div',
)<StyledFullWidthBreachWallBackgroundProps>(
    ({ isFullWidthBelowDesktop, breachType }) => [
        isFullWidthBelowDesktop && {
            marginLeft: calcRem(-15),
            marginRight: calcRem(-15),
            [breakpoint('md')]: {
                marginLeft: calcRem(0),
                marginRight: calcRem(0),
            },
        },
    ],
)

interface StyledBreachWallContent2022Props {
    largeImage?: string
    smallImage?: string
    breachType: OfferVariantType
}
export const StyledBreachWallContent2022 = styled(
    StyledBreachWallContent,
)<StyledBreachWallContent2022Props>(
    ({ largeImage, smallImage, breachType }) => [
        {
            padding: calcRem(48, 24, 24, 18),
            flexDirection: 'column',
            justifyContent: 'center',
            gap: calcRem(48),

            [breakpoint('xxs')]: {
                padding: calcRem(40, 24),
                gap: calcRem(32),
            },
        },
        // Check if the small image or large image is defined
        smallImage && {
            backgroundBlendMode: 'revert',
            backgroundImage: `url('${smallImage}')`,
            backgroundSize: '100% auto',
            overflow: 'hidden',
        },
        largeImage && {
            [breakpoint('sm')]: {
                backgroundImage: `url('${largeImage}')`,
                backgroundSize: 'cover',
                backgroundPosition: 'right',
            },
        },
        // PROMO: 50 Off Campaign
        breachType === '50-off-campaign' && {
            background:
                'linear-gradient(200deg, #00074F 8%, #022195 47%, #043DE2 109%)',
            backgroundSize: 'cover',
            padding: calcRem(30, 24, 24, 24),
            [breakpoint('sm')]: {
                backgroundImage: `url('${largeImage}'), linear-gradient(200deg, #00074F 8%, #022195 47%, #043DE2 109%)`,
                backgroundSize: '50% 50%, cover',
                backgroundPosition: 'right bottom, center',
                backgroundRepeat: 'no-repeat, no-repeat',
            },
        },
        // PROMO: Footy Lives Here
        breachType === 'afl-footy-lives-here-breach-screen' && {
            padding: calcRem(48, 0),
            backgroundColor: '#000',
            background: `linear-gradient(to bottom, rgba(0,0,0,0) 15%, rgba(0,0,0,1) 20%, rgba(0,0,0,1.00) 100%), ${
                smallImage ? `url('${smallImage}')` : undefined
            }`,
            backgroundSize: '100%, 100%',
            backgroundPosition: 'top',
            backgroundRepeat: 'no-repeat',
            marginTop: calcRem(36),
            gap: calcRem(48),

            // Need to override largeImage styles at this breakpoint or it uses the wrong image
            [breakpoint('sm')]: {
                backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0) 15%, rgba(0,0,0,1) 20%, rgba(0,0,0,1.00) 100%), url('${largeImage}')`,
                backgroundSize: '100%, 100%',
                backgroundPosition: 'top',
            },

            [breakpoint('lg')]: {
                padding: calcRem(48, 0),
                marginTop: 0,
                backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0) 15%, rgba(0,0,0,1) 60%, rgba(0,0,0,1.00) 100%), ${
                    largeImage ? `url('${largeImage}')` : undefined
                }`,
                backgroundPosition: 'top',
                backgroundRepeat: 'no-repeat',
            },
        },
    ],
)

export const StyledRegistrationInfo = styled('div')<{
    breachType?: OfferVariantType
}>(({ breachType }) => [
    {
        width: '100%',
        display: 'block',
        textAlign: 'center',
    },
    // PROMO: Footy Lives Here
    breachType === 'afl-footy-lives-here-breach-screen' && {
        display: 'flex',
        justifyContent: 'flex-end',
    },
])

export const StyledTitle = styled('h2')<{ breachType?: OfferVariantType }>(
    ({ theme, breachType }) => [
        {
            fontFamily: fonts.thewest.serif,
            fontWeight: 500,
            textAlign: 'center',
            lineHeight: calcRem(32),
            fontSize: calcRem(28),
            color: tokens.thewest.colors.palette.black,

            margin: 0,
            maxWidth: calcRem(400),

            [breakpoint('xs')]: {
                lineHeight: calcRem(40),
                fontSize: calcRem(32),
            },
        },
    ],
)

export const StyledTitleImage = styled('img')<{
    breachType?: OfferVariantType
}>(({ breachType }) => [
    // Base styles for all packages
    {
        width: '100%',
        padding: calcRem(32, 0, 0),

        [breakpoint('xs')]: {
            padding: calcRem(32),
            width: '100%',
            maxWidth: 'none',
        },
    },
    // PROMO: Footy Lives Here
    breachType === 'afl-footy-lives-here-breach-screen' && {
        padding: 0,
        width: '60%',

        [breakpoint('xs')]: {
            padding: 0,
            paddingLeft: calcRem(100),
            maxWidth: calcRem(500),
        },

        [breakpoint('lg')]: {
            padding: 0,
            width: '100%',
        },
    },
    // PROMO: 50% Off Campaign
    breachType === '50-off-campaign' && {
        padding: 0,
        width: '100%',
        maxWidth: calcRem(344),
        justifySelf: 'center',

        [breakpoint('xs')]: {
            padding: 0,
            width: '40%',
            justifySelf: 'center',
        },
    },
])

export const StyledFooterImage = styled('img')(() => ({
    width: '100%',
    maxWidth: calcRem(240),
    margin: calcRem(48, 70, 0, 70),

    [breakpointMax('xs')]: {
        width: 'Calc(100% - 50px)',
    },
}))

export const StyledMessage = styled('div')<BreachTypeProps>(({ theme }) => [
    {
        margin: 'auto',
        marginBottom: calcRem(theme.margins.sm),
        maxWidth: calcRem(600),

        p: {
            margin: calcRem(0, theme.margins.sm),
            fontSize: calcRem(20),
            fontWeight: 'normal',
            textAlign: 'center',

            '&.full-width': {
                fontWeight: 'normal',
                width: '100%',
            },
        },
    },
])

export const StyledErrorIconContainer = styled.div(({ theme }) => ({
    display: 'flex',
    padding: calcRem(12),
    borderRadius: calcRem(16),
    background: ' rgba(4, 61, 226, 0.06)',
    width: 'fit-content',

    svg: {
        width: 20,
        height: 20,
        color: theme.colors.brand,
    },
}))

export const StyledErrorContainer = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: calcRem(16),

    span: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        gap: calcRem(8),
    },
    h2: {
        color: theme.colors.components?.emailPreferenceCentre?.text.default,
        textAlign: 'center',
        fontSize: calcRem(20),
        fontWeight: 600,
        lineHeight: '120%',
        margin: 0,
    },
    p: {
        color: theme.colors.text.secondary,
        textAlign: 'center',
        fontSize: calcRem(16),
        lineHeight: '150%',
        margin: 0,
    },
    button: {
        display: 'flex',
        padding: calcRem(12, 24),
        borderRadius: calcRem(2),
        backgroundColor: theme.colors.actions.button.primary.background.default,
        color: theme.colors.actions.button.primary.text.default,
        border: 'none',
        cursor: 'pointer',

        '&:focus,&:hover': {
            opacity: 0.8,
        },

        transition: 'opacity 0.25s',
    },
}))

export const StyledIconSpinner = styled(IconSpinner)(({ theme }) => ({
    color: theme.colors.brand,
    width: 24,
    height: 24,

    [breakpoint('md')]: {
        width: 48,
        height: 48,
    },
}))

export const StyledPackagesContainer = styled.div<BreachTypeProps>(
    ({ breachType, useGridLayout, numberOfColumns }) => [
        // Shared properties for all breach types
        {
            position: 'relative',
            gap: calcRem(16, 12),
            width: '100%',
            marginTop: calcRem(0),

            display: 'grid',
            justifyContent: 'center',
            alignItems: 'center',

            [breakpoint('sm')]: {
                marginTop: calcRem(24),
            },
        },
        breachType === '50-off-campaign' &&
            useGridLayout &&
            numberOfColumns && {
                [breakpoint('sm')]: {
                    gridTemplateColumns: `repeat(${numberOfColumns}, 1fr)`,
                    gridTemplateRows: '1fr',
                    gap: calcRem(24), // 32 - 8px outline

                    // Not baseline widely available, but will help with tab order on supported browsers
                    readingFlow: 'grid-columns',
                },
            },
        // If useGridLayout is true and numberOfColumns is provided, use CSS Grid to layout the packages based on that number
        useGridLayout &&
            numberOfColumns && {
                display: 'grid',
                // On mobile and tablet, stack the packages vertically
                gridTemplateColumns: `1fr`,
                gridTemplateRows: `repeat(${numberOfColumns}, auto)`,
                placeItems: 'center',
                gap: calcRem(16), // 24 - 8px outline

                // Not baseline widely available, but will help with tab order on supported browsers
                readingFlow: 'grid-rows',

                [breakpoint('xs')]: {
                    display: 'grid',
                },

                // On desktop, set the number of columns based on numberOfColumns prop
                [breakpoint('lg')]: {
                    gridTemplateColumns: `repeat(${numberOfColumns}, 1fr)`,
                    gridTemplateRows: '1fr',
                    gap: calcRem(24), // 32 - 8px outline

                    // Not baseline widely available, but will help with tab order on supported browsers
                    readingFlow: 'grid-columns',
                },
            },
        // PROMO: Footy Lives Here
        breachType === 'afl-footy-lives-here-breach-screen' && {
            padding: calcRem(0, 16),

            [breakpoint('lg')]: {
                padding: calcRem(0, 24),
            },
        },
    ],
)
export const StyledFooterAndTermsContainer = styled.div<BreachTypeProps>(() => [
    {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: calcRem(48),

        [breakpoint('xs')]: {
            gap: calcRem(32),
        },
    },
])

export const StyledButton = styled('button')(({ theme }) => ({
    display: 'inline-block',
    border: `1px solid ${colors.white}`,
    padding: calcRem(theme.margins.sm, theme.margins.xl),
    marginTop: calcRem(theme.margins.lg),
    background: 'none',
    color: colors.white,
    borderRadius: calcRem(2),
    fontFamily: theme.fonts.sansSerif,
    fontWeight: 700,
    fontSize: calcRem(13),
    textDecoration: 'none',
    textTransform: 'uppercase',
    cursor: 'pointer',
    ...transition({ properties: ['background', 'color'] }),

    '&:hover': {
        backgroundColor: colors.white,
        color: themedValue(theme, {
            thewest: colors.thewest.greyGorilla,
            perthnow: colors.perthnow.blueKyanite,
            sevennews: colors.sevennews.red,
            fallback: undefined,
        }),
    },

    '&:focus': {
        backgroundColor: themedValue(theme, {
            thewest: colors.thewest.greyGorilla,
            perthnow: colors.perthnow.pinkThulite,
            sevennews: colors.sevennews.red,
            fallback: undefined,
        }),
        borderColor: themedValue(theme, {
            thewest: colors.thewest.greyGorilla,
            perthnow: colors.perthnow.pinkThulite,
            sevennews: colors.sevennews.red,
            fallback: undefined,
        }),
        color: 'currentColor',
        outline: 'none',
        textDecoration: 'underline',
    },
}))

// --------------- TERMS STYLES ---------------

export const StyledTerms2022 = styled.span<BreachTypeProps>(
    ({ theme, breachType }) => [
        {
            flex: '0 1 100%',
            border: 'none',
            borderRadius: calcRem(5),
            fontSize: calcRem(16),
            textAlign: 'center',
            textTransform: 'initial',
            justifySelf: 'center',
            width: '100%',
            padding: calcRem(16),

            backgroundColor: theme.colors.background.primary,
            color: theme.colors.text.primary,
            fontFamily: theme.fonts.serif,

            [breakpoint('sm')]: {
                fontSize: calcRem(16),
            },

            '& a': {
                color: tokens.thewest.colors.actions.link.default,
            },
        },
        // PROMO: Footy Lives Here
        breachType === 'afl-footy-lives-here-breach-screen' && {
            background: 'none',
            fontFamily: theme.fonts.sansSerif,
            padding: 0,
            color: tokens.thewest.colors.palette.white,

            a: {
                color: tokens.thewest.colors.palette.white,
            },
        },
    ],
)
export const StyledTerms = styled('p')(({ theme }) => ({
    color: colors.white,
    margin: 0,
    marginTop: calcRem(theme.margins.sm),
    fontSize: calcRem(11),
}))

export const StyledTextLink = styled(TextLink)<{ reverseColor: boolean }>(
    ({ reverseColor }) => ({
        color: reverseColor ? colors.white : undefined,
    }),
)
